import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/shared/hooks/use-toast";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Trash2, Plus, Users } from "lucide-react";
import {
  fetchTeamMembersByClaim,
  createTeamMember,
  createTeamMemberForClaim,
  deleteTeamMember,
  TeamMember,
} from "../api/team-members";
import { createSupplierPortalClient } from "../api/supplier-portal";

interface TeamMemberFormProps {
  claimId: string;
  tenantId?: string; // Optional - if not provided, will be fetched from claim
  supplierToken?: string;
}

export function TeamMemberForm({ claimId, tenantId, supplierToken }: TeamMemberFormProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [newMember, setNewMember] = useState({ name: "", role: "", email: "" });
  const supplierClient = supplierToken ? createSupplierPortalClient(supplierToken) : null;

  const { data: teamMembers, isLoading } = useQuery({
    queryKey: ["mod_8d_team_members", claimId],
    queryFn: () => supplierClient?.fetchTeamMembers() ?? fetchTeamMembersByClaim(claimId),
  });

  const addMemberMutation = useMutation({
    mutationFn: async () => {
      const memberData = {
        name: newMember.name.trim(),
        role: newMember.role.trim(),
        email: newMember.email.trim() || null,
      };

      if (supplierClient) {
        await supplierClient.createTeamMember(memberData);
      } else if (tenantId) {
        // Use direct create if we have tenant_id
        await createTeamMember({
          tenant_id: tenantId,
          claim_id: claimId,
          ...memberData,
        });
      } else {
        // Fetch tenant_id from claim if not provided
        await createTeamMemberForClaim(claimId, memberData);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mod_8d_team_members", claimId] });
      setNewMember({ name: "", role: "", email: "" });
      toast({ title: "Team member added" });
    },
    onError: () => {
      toast({ title: "Failed to add team member", variant: "destructive" });
    },
  });

  const deleteMemberMutation = useMutation({
    mutationFn: async (memberId: string) => {
      if (supplierClient) {
        await supplierClient.deleteTeamMember(memberId);
      } else {
        await deleteTeamMember(memberId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mod_8d_team_members", claimId] });
      toast({ title: "Team member removed" });
    },
  });

  const canAddMember = newMember.name.trim() && newMember.role.trim();

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
        <Users className="h-4 w-4" />
        <span>Add team members responsible for this 8D report</span>
      </div>

      {/* Existing team members */}
      {teamMembers && teamMembers.length > 0 && (
        <div className="space-y-2">
          {teamMembers.map((member) => (
            <div
              key={member.id}
              className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg border border-border"
            >
              <div className="grid grid-cols-3 gap-4 flex-1 min-w-0">
                <div className="truncate">
                  <span className="text-xs text-muted-foreground block">Name</span>
                  <span className="text-sm font-medium">{member.name}</span>
                </div>
                <div className="truncate">
                  <span className="text-xs text-muted-foreground block">Role</span>
                  <span className="text-sm">{member.role}</span>
                </div>
                <div className="truncate">
                  <span className="text-xs text-muted-foreground block">Email</span>
                  <span className="text-sm">{member.email || "-"}</span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                onClick={() => deleteMemberMutation.mutate(member.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Add new team member form */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 bg-muted/30 rounded-lg border border-dashed border-border">
        <div className="space-y-1.5">
          <Label htmlFor="member-name" className="text-xs">
            Name *
          </Label>
          <Input
            id="member-name"
            placeholder="Full name"
            value={newMember.name}
            onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="member-role" className="text-xs">
            Role *
          </Label>
          <Input
            id="member-role"
            placeholder="e.g. Quality Engineer"
            value={newMember.role}
            onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="member-email" className="text-xs">
            Email
          </Label>
          <Input
            id="member-email"
            type="email"
            placeholder="email@example.com"
            value={newMember.email}
            onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
          />
        </div>
      </div>

      <Button
        size="sm"
        disabled={!canAddMember || addMemberMutation.isPending}
        onClick={() => addMemberMutation.mutate()}
      >
        <Plus className="h-4 w-4 mr-1" />
        Add Team Member
      </Button>

      {isLoading && (
        <p className="text-sm text-muted-foreground">Loading team members...</p>
      )}
    </div>
  );
}
